/* eslint-disable no-console */
import React, { ReactElement, useState, useEffect } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  Chip,
} from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import { useForm } from '@tanstack/react-form'

import { useAppDispatch } from 'src/store/hooks'
import {
  createANCCAccreditation,
  fetchANCCAccreditation,
} from 'src/v3/content-pipeline/event-pipeline.slice'
import { modalInputSx, modalPaperProps } from 'src/v3/utilities/modal-styles'

type ANCCCreationFormProps = {
  open: boolean
  onClose: () => void
  eventID: string
  eventName?: string
}

let targetAudiences = [
  'Registered Nurse',
  'Paramedic',
  'EMT',
  'Respiratory Therapist',
  'Advanced Practice Nurse',
  'Other',
]

let educationalNeeds = ['Knowledge', 'Skill', 'Practice']

export default ({
  open,
  onClose,
  eventID,
  eventName,
}: ANCCCreationFormProps): ReactElement => {
  let dispatch = useAppDispatch()
  let [loading, setLoading] = useState(false)

  let form = useForm({
    defaultValues: {
      courseCode: '',
      expirationDate: '',
      targetAudience: [],
      educationalNeeds: [],
      competency: '',
      professionalSource: '',
    },
    onSubmit: async ({ value }) => {
      setLoading(true)
      try {
        // Convert date string to ISO format
        let formattedData = {
          eventID,
          courseCode: value.courseCode,
          expirationDate: value.expirationDate
            ? new Date(value.expirationDate).toISOString()
            : '',
          targetAudience: value.targetAudience,
          educationalNeeds: value.educationalNeeds,
          competency: value.competency || undefined,
          professionalSource: value.professionalSource || undefined,
        }

        // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
        await dispatch(createANCCAccreditation(formattedData))
        // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
        await dispatch(fetchANCCAccreditation(eventID))
        form.reset()
        setLoading(false)
        onClose()
      } catch (error) {
        console.error('Failed to create ANCC accreditation:', error)
      } finally {
        setLoading(false)
      }
    },
  })

  useEffect(() => {
    if (open) {
      // Set default expiration date to 2 years from now
      let twoYears = new Date(Date.now() + 2 * 365 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split('T')[0]
      form.setFieldValue('expirationDate', twoYears)
    }
  }, [open])

  return (
    <Dialog
      open={open}
      onClose={() => {
        form.reset()
        setLoading(false)
        onClose()
      }}
      fullWidth
      slotProps={{
        paper: {
          ...modalPaperProps,
          sx: { ...modalPaperProps.sx, borderRadius: 2 },
        },
      }}
      maxWidth="md"
    >
      <DialogTitle>Create ANCC Accreditation</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'grid', gap: 3, mt: 1 }}>
          <Typography color="text.secondary" variant="body2">
            Create ANCC accreditation for {eventName || 'this event'}. Course
            code, expiration date, target audience, and educational needs are
            required for compliance approval.
          </Typography>

          <form.Field
            name="courseCode"
            children={(field) => (
              <TextField
                label="Course Code"
                name={field.name}
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                error={field.state.meta.errors.length > 0}
                helperText={field.state.meta.errors[0]}
                required
                fullWidth
                sx={modalInputSx}
                placeholder="e.g., ANCC12345"
              />
            )}
          />

          <form.Field
            name="expirationDate"
            children={(field) => (
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Expiration Date"
                  value={field.state.value ? dayjs(field.state.value) : null}
                  onChange={(val) => {
                    if (!val) {
                      field.handleChange('')
                    } else {
                      field.handleChange(val.format('YYYY-MM-DD'))
                    }
                  }}
                  onClose={field.handleBlur}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      sx: modalInputSx,
                      error: field.state.meta.errors.length > 0,
                      helperText: field.state.meta.errors[0],
                      onBlur: field.handleBlur,
                    },
                  }}
                />
              </LocalizationProvider>
            )}
          />

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <form.Field
              name={'targetAudience' as any}
              children={(field) => (
                <FormControl fullWidth required>
                  <InputLabel>Target Audience</InputLabel>
                  <Select
                    multiple
                    value={field.state.value}
                    label="Target Audience"
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    sx={modalInputSx}
                    input={<OutlinedInput label="Target Audience" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {Array.isArray(selected) &&
                          selected.map((value) => (
                            <Chip key={value} label={value} size="small" />
                          ))}
                      </Box>
                    )}
                  >
                    {targetAudiences.map((audience) => (
                      <MenuItem key={audience} value={audience}>
                        {audience}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />

            <form.Field
              name={'educationalNeeds' as any}
              children={(field) => (
                <FormControl fullWidth required>
                  <InputLabel>Educational Needs</InputLabel>
                  <Select
                    multiple
                    value={field.state.value}
                    label="Educational Needs"
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    sx={modalInputSx}
                    input={<OutlinedInput label="Educational Needs" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {Array.isArray(selected) &&
                          selected.map((value) => (
                            <Chip key={value} label={value} size="small" />
                          ))}
                      </Box>
                    )}
                  >
                    {educationalNeeds.map((need) => (
                      <MenuItem key={need} value={need}>
                        {need}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </Box>

          <form.Field
            name="competency"
            children={(field) => (
              <TextField
                multiline
                minRows={3}
                label="Competency (Optional)"
                name={field.name}
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                error={field.state.meta.errors.length > 0}
                helperText={field.state.meta.errors[0]}
                fullWidth
                sx={modalInputSx}
                placeholder="Clinical competency standards"
              />
            )}
          />

          <form.Field
            name="professionalSource"
            children={(field) => (
              <TextField
                label="Professional Source (Optional)"
                name={field.name}
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                error={field.state.meta.errors.length > 0}
                helperText={field.state.meta.errors[0]}
                fullWidth
                multiline
                minRows={3}
                sx={modalInputSx}
                placeholder="American Nurses Association or other professional source"
              />
            )}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            form.reset()
            setLoading(false)
            onClose()
          }}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          onClick={() => form.handleSubmit()}
          variant="contained"
          disabled={loading}
        >
          {loading ? 'Creating...' : 'Create ANCC Accreditation'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
